/**
 * InforCSDConnector — Infor CloudSuite Distribution adapter.
 *
 * Keyston Bros runs on Infor CSD. This is the highest-priority demo connector // foundation-purity-allow: docstring names the demo use case; customer identity is the connector's demo context
 * in the integration layer. Production-quality mock-mode with demo tenant
 * pre-configuration.
 *
 * Extends IntegrationService via the registry (architecture rule: no base class
 * inheritance — registry pattern only).
 *
 * Bidirectional sync scope:
 *   Pull: item master, customer master, vendor master, inventory positions,
 *         purchase orders, sales orders, invoices
 *   Push: AEGIS-approved replenishment POs → Infor CSD via REST API
 *
 * Key Keyston Bros mappings: // foundation-purity-allow: docstring section describing customer-specific field mappings; customer identity is the mapping context
 *   Infor branch codes  → Kairos warehouse IDs
 *   Infor item classes  → IMS specialty material flags
 *   Pack size configs   → IMS packSizes array
 *   Roll/linear flag    → WMS-SP specialty handling
 */

import IntegrationService, { isMockMode } from '../IntegrationService';
import DataService from '../DataService';
import EventBusService from '../EventBusService';

const ENV_KEY = 'INFOR_CSD_API_KEY';
const SOURCE = 'infor_csd';

// ── Field presets ─────────────────────────────────────────────────────────────

export const INFOR_FIELD_PRESETS = [
  { sourceField: 'ITEM_NUMBER',     kairosField: 'items.sku',           transform: 'trim' },
  { sourceField: 'ITEM_DESC',       kairosField: 'items.description' },
  { sourceField: 'ITEM_CLASS',      kairosField: 'items.category' },
  { sourceField: 'UOM_BASE',        kairosField: 'items.uomBase',        transform: 'upper' },
  { sourceField: 'UOM_PACK',        kairosField: 'items.uomPack' },
  { sourceField: 'PACK_SIZE',       kairosField: 'items.packSize' },
  { sourceField: 'REORDER_POINT',   kairosField: 'items.reorderPoint' },
  { sourceField: 'SAFETY_STOCK',    kairosField: 'items.safetyStock' },
  { sourceField: 'QTY_ON_HAND',     kairosField: 'items.onHand' },
  { sourceField: 'QTY_RESERVED',    kairosField: 'items.reserved' },
  { sourceField: 'QTY_ON_ORDER',    kairosField: 'items.onOrder' },
  { sourceField: 'STD_COST',        kairosField: 'items.cost' },
  { sourceField: 'LIST_PRICE',      kairosField: 'items.price' },
  { sourceField: 'WAREHOUSE',       kairosField: 'items.warehouseId' },
  { sourceField: 'SPECIALTY_FLAG',  kairosField: 'items.specialtyMaterial' },
  { sourceField: 'ROLL_LINEAR',     kairosField: 'items.rollLinearGoods' },
  { sourceField: 'CUST_NUMBER',     kairosField: 'customers.externalId' },
  { sourceField: 'CUST_NAME',       kairosField: 'customers.name' },
  { sourceField: 'CUST_EMAIL',      kairosField: 'customers.email' },
  { sourceField: 'VEND_NUMBER',     kairosField: 'vendors.externalId' },
  { sourceField: 'VEND_NAME',       kairosField: 'vendors.name' },
  { sourceField: 'PO_NUMBER',       kairosField: 'purchase_orders.poNumber' },
  { sourceField: 'SO_NUMBER',       kairosField: 'sales_orders.soNumber' },
  { sourceField: 'INV_NUMBER',      kairosField: 'invoices.invoiceNumber' },
];

// ── Branch/warehouse mapping — Infor branch code → Kairos warehouse ID ───────

export const INFOR_BRANCH_MAP = {
  'DEN': { warehouseId: 'WH-DEN-01', name: 'Denver Distribution Center',    city: 'Denver',    state: 'CO', timezone: 'America/Denver' },
  'PHX': { warehouseId: 'WH-PHX-01', name: 'Phoenix Regional Warehouse',    city: 'Phoenix',   state: 'AZ', timezone: 'America/Phoenix' },
  'ATL': { warehouseId: 'WH-ATL-01', name: 'Atlanta Southeast Hub',         city: 'Atlanta',   state: 'GA', timezone: 'America/New_York' },
  'DAL': { warehouseId: 'WH-DAL-01', name: 'Dallas-Fort Worth Depot',       city: 'Dallas',    state: 'TX', timezone: 'America/Chicago' },
  'CHI': { warehouseId: 'WH-CHI-01', name: 'Chicago Midwest Center',        city: 'Chicago',   state: 'IL', timezone: 'America/Chicago' },
  'LAX': { warehouseId: 'WH-LAX-01', name: 'Los Angeles West Coast Hub',    city: 'Commerce',  state: 'CA', timezone: 'America/Los_Angeles' },
};

// ── Mock: item master — production-quality Keyston Bros catalog ──────────────── // foundation-purity-allow: section comment for the Keyston demo item master; customer name identifies the demo fixture set
//
// KEY ITEM: KB-FAB-MERI-004 — Meridian Chenille fabric with 5-lot, 4YD each.
// This is the exact scenario used in the Keyston Bros demo. // foundation-purity-allow: comment names the demo scenario; customer name is the demo fixture context

const MOCK_ITEMS = [
  // ── LEATHER ────────────────────────────────────────────────────────────────
  {
    sku: 'KB-LEA-1001', itemNo: 'KB-LEA-1001',
    description: 'Premium Italian Leather Hide — Natural Tan',
    category: 'leather', itemClass: 'HIDE', uomBase: 'HIDE', uomPack: 'EA',
    packSize: 1, reorderPoint: 40, safetyStock: 20, onHand: 84, reserved: 12, onOrder: 0,
    cost: 189.50, price: 329.00, warehouseId: 'DEN', branchCode: 'DEN',
    supplierInforId: 'VEND-0077', specialtyMaterial: true, rollLinearGoods: false,
  },
  {
    sku: 'KB-LEA-1002', itemNo: 'KB-LEA-1002',
    description: 'Chrome-Tanned Leather Hide — Chocolate',
    category: 'leather', itemClass: 'HIDE', uomBase: 'HIDE', uomPack: 'EA',
    packSize: 1, reorderPoint: 35, safetyStock: 15, onHand: 27, reserved: 8, onOrder: 20,
    cost: 175.00, price: 299.00, warehouseId: 'DEN', branchCode: 'DEN',
    supplierInforId: 'VEND-0077', specialtyMaterial: true, rollLinearGoods: false,
  },
  {
    sku: 'KB-LEA-1003', itemNo: 'KB-LEA-1003',
    description: 'Bovine Top-Grain Roll — Black 50yd',
    category: 'leather', itemClass: 'ROLL', uomBase: 'ROLL', uomPack: 'EA',
    packSize: 1, reorderPoint: 20, safetyStock: 10, onHand: 33, reserved: 5, onOrder: 0,
    cost: 410.00, price: 689.00, warehouseId: 'DEN', branchCode: 'DEN',
    supplierInforId: 'VEND-0088', specialtyMaterial: true, rollLinearGoods: true,
  },
  // ── VINYL ─────────────────────────────────────────────────────────────────
  {
    sku: 'KB-VIN-2005', itemNo: 'KB-VIN-2005',
    description: 'Marine-Grade Vinyl — White 54"',
    category: 'vinyl', itemClass: 'FABRIC', uomBase: 'YD', uomPack: 'BOX',
    packSize: 50, reorderPoint: 300, safetyStock: 100, onHand: 520, reserved: 80, onOrder: 0,
    cost: 6.25, price: 11.95, warehouseId: 'DEN', branchCode: 'DEN',
    supplierInforId: 'VEND-0091', specialtyMaterial: false, rollLinearGoods: true,
  },
  {
    sku: 'KB-VIN-2006', itemNo: 'KB-VIN-2006',
    description: 'Marine-Grade Vinyl — Navy 54"',
    category: 'vinyl', itemClass: 'FABRIC', uomBase: 'YD', uomPack: 'BOX',
    packSize: 50, reorderPoint: 300, safetyStock: 100, onHand: 180, reserved: 30, onOrder: 100,
    cost: 6.25, price: 11.95, warehouseId: 'DEN', branchCode: 'DEN',
    supplierInforId: 'VEND-0091', specialtyMaterial: false, rollLinearGoods: true,
  },
  {
    sku: 'KB-VIN-2007', itemNo: 'KB-VIN-2007',
    description: 'Marine-Grade Vinyl — Burgundy 54"',
    category: 'vinyl', itemClass: 'FABRIC', uomBase: 'YD', uomPack: 'BOX',
    packSize: 50, reorderPoint: 200, safetyStock: 75, onHand: 340, reserved: 50, onOrder: 0,
    cost: 6.50, price: 12.50, warehouseId: 'DEN', branchCode: 'DEN',
    supplierInforId: 'VEND-0091', specialtyMaterial: false, rollLinearGoods: true,
  },
  // ── UPHOLSTERY FABRIC ─────────────────────────────────────────────────────
  {
    sku: 'KB-FAB-3012', itemNo: 'KB-FAB-3012',
    description: 'Upholstery Fabric — Sunbrella Canvas 54"',
    category: 'fabric', itemClass: 'FABRIC', uomBase: 'YD', uomPack: 'ROLL',
    packSize: 30, reorderPoint: 150, safetyStock: 75, onHand: 240, reserved: 40, onOrder: 0,
    cost: 18.40, price: 34.50, warehouseId: 'DEN', branchCode: 'DEN',
    supplierInforId: 'VEND-0102', specialtyMaterial: false, rollLinearGoods: true,
  },
  {
    sku: 'KB-FAB-3013', itemNo: 'KB-FAB-3013',
    description: 'Upholstery Fabric — Crypton Oat 54"',
    category: 'fabric', itemClass: 'FABRIC', uomBase: 'YD', uomPack: 'ROLL',
    packSize: 30, reorderPoint: 120, safetyStock: 60, onHand: 190, reserved: 25, onOrder: 60,
    cost: 22.00, price: 41.00, warehouseId: 'DEN', branchCode: 'DEN',
    supplierInforId: 'VEND-0102', specialtyMaterial: false, rollLinearGoods: true,
  },
  // ── MERIDIAN CHENILLE — KEY DEMO ITEM (SKU-004) ───────────────────────────
  // Pack size: 5-lot, 4YD each → packSize: 4 YD per lot, lotsPerPack: 5
  // Roll/linear goods: true → specialty handling in WMS-SP
  // Specialty material flag: true
  {
    sku: 'KB-FAB-MERI-004', itemNo: 'KB-FAB-MERI-004',
    description: 'Meridian Chenille — Slate Blue 54" (4YD lots)',
    category: 'fabric', itemClass: 'SPECIALTY', uomBase: 'YD', uomPack: 'LOT',
    packSize: 4,         // 4 YD per lot
    lotsPerPack: 5,      // 5-lot pack scenario for the demo
    reorderPoint: 100, safetyStock: 40, onHand: 78, reserved: 20, onOrder: 0,
    cost: 24.80, price: 46.00, warehouseId: 'DEN', branchCode: 'DEN',
    supplierInforId: 'VEND-0102',
    specialtyMaterial: true,
    rollLinearGoods: true,
    wmsSpecialtyHandling: 'WMS-SP',     // WMS-SP specialty handling flag
    demoHighlight: true,                // surfaced in demo connector card
    demoNote: '5-lot, 4YD each — the Keyston Bros Meridian Chenille demo scenario', // foundation-purity-allow: demo metadata field; customer name identifies the demo scenario in the item record
  },
  {
    sku: 'KB-FAB-MERI-005', itemNo: 'KB-FAB-MERI-005',
    description: 'Meridian Chenille — Charcoal 54" (4YD lots)',
    category: 'fabric', itemClass: 'SPECIALTY', uomBase: 'YD', uomPack: 'LOT',
    packSize: 4, lotsPerPack: 5,
    reorderPoint: 80, safetyStock: 30, onHand: 52, reserved: 12, onOrder: 20,
    cost: 24.80, price: 46.00, warehouseId: 'DEN', branchCode: 'DEN',
    supplierInforId: 'VEND-0102', specialtyMaterial: true, rollLinearGoods: true, wmsSpecialtyHandling: 'WMS-SP',
  },
  {
    sku: 'KB-FAB-MERI-006', itemNo: 'KB-FAB-MERI-006',
    description: 'Meridian Chenille — Ivory 54" (4YD lots)',
    category: 'fabric', itemClass: 'SPECIALTY', uomBase: 'YD', uomPack: 'LOT',
    packSize: 4, lotsPerPack: 5,
    reorderPoint: 60, safetyStock: 25, onHand: 28, reserved: 8, onOrder: 40,
    cost: 24.80, price: 46.00, warehouseId: 'DEN', branchCode: 'DEN',
    supplierInforId: 'VEND-0102', specialtyMaterial: true, rollLinearGoods: true, wmsSpecialtyHandling: 'WMS-SP',
  },
  // ── FOAM & PADDING ────────────────────────────────────────────────────────
  {
    sku: 'KB-FOAM-4001', itemNo: 'KB-FOAM-4001',
    description: 'High-Density Foam Block — 4in',
    category: 'foam', itemClass: 'FOAM', uomBase: 'EA', uomPack: 'CASE',
    packSize: 12, reorderPoint: 60, safetyStock: 30, onHand: 48, reserved: 6, onOrder: 0,
    cost: 8.10, price: 16.25, warehouseId: 'DEN', branchCode: 'DEN',
    supplierInforId: 'VEND-0110', specialtyMaterial: false, rollLinearGoods: false,
  },
  {
    sku: 'KB-FOAM-4002', itemNo: 'KB-FOAM-4002',
    description: 'High-Density Foam Block — 6in',
    category: 'foam', itemClass: 'FOAM', uomBase: 'EA', uomPack: 'CASE',
    packSize: 12, reorderPoint: 48, safetyStock: 24, onHand: 36, reserved: 4, onOrder: 0,
    cost: 11.50, price: 22.00, warehouseId: 'DEN', branchCode: 'DEN',
    supplierInforId: 'VEND-0110', specialtyMaterial: false, rollLinearGoods: false,
  },
  // ── THREAD ───────────────────────────────────────────────────────────────
  {
    sku: 'KB-THR-5003', itemNo: 'KB-THR-5003',
    description: 'Marine Polyester Thread — White 6000yd',
    category: 'thread', itemClass: 'NOTION', uomBase: 'SPL', uomPack: 'EA',
    packSize: 1, reorderPoint: 120, safetyStock: 50, onHand: 87, reserved: 10, onOrder: 0,
    cost: 14.80, price: 27.50, warehouseId: 'DEN', branchCode: 'DEN',
    supplierInforId: 'VEND-0115', specialtyMaterial: false, rollLinearGoods: false,
  },
  // ── HARDWARE ──────────────────────────────────────────────────────────────
  {
    sku: 'KB-ZIP-6001', itemNo: 'KB-ZIP-6001',
    description: 'YKK #10 Zipper — Black 1yd',
    category: 'hardware', itemClass: 'NOTION', uomBase: 'EA', uomPack: 'BOX',
    packSize: 100, reorderPoint: 400, safetyStock: 150, onHand: 620, reserved: 80, onOrder: 0,
    cost: 0.85, price: 1.95, warehouseId: 'DEN', branchCode: 'DEN',
    supplierInforId: 'VEND-0118', specialtyMaterial: false, rollLinearGoods: false,
  },
  {
    sku: 'KB-HRD-7004', itemNo: 'KB-HRD-7004',
    description: 'Brass Grommets #0 — Bag of 144',
    category: 'hardware', itemClass: 'NOTION', uomBase: 'BAG', uomPack: 'EA',
    packSize: 1, reorderPoint: 40, safetyStock: 20, onHand: 61, reserved: 8, onOrder: 0,
    cost: 22.00, price: 38.50, warehouseId: 'DEN', branchCode: 'DEN',
    supplierInforId: 'VEND-0118', specialtyMaterial: false, rollLinearGoods: false,
  },
  // PHX branch items — multi-branch demo
  {
    sku: 'KB-VIN-2005', itemNo: 'KB-VIN-2005-PHX',
    description: 'Marine-Grade Vinyl — White 54" (PHX)',
    category: 'vinyl', itemClass: 'FABRIC', uomBase: 'YD', uomPack: 'BOX',
    packSize: 50, reorderPoint: 150, safetyStock: 50, onHand: 280, reserved: 40, onOrder: 0,
    cost: 6.25, price: 11.95, warehouseId: 'PHX', branchCode: 'PHX',
    supplierInforId: 'VEND-0091', specialtyMaterial: false, rollLinearGoods: true,
  },
  {
    sku: 'KB-FAB-MERI-004', itemNo: 'KB-FAB-MERI-004-PHX',
    description: 'Meridian Chenille — Slate Blue 54" (PHX, 4YD lots)',
    category: 'fabric', itemClass: 'SPECIALTY', uomBase: 'YD', uomPack: 'LOT',
    packSize: 4, lotsPerPack: 5,
    reorderPoint: 40, safetyStock: 16, onHand: 24, reserved: 8, onOrder: 20,
    cost: 24.80, price: 46.00, warehouseId: 'PHX', branchCode: 'PHX',
    supplierInforId: 'VEND-0102', specialtyMaterial: true, rollLinearGoods: true, wmsSpecialtyHandling: 'WMS-SP',
  },
];

// ── Mock: inventory positions per branch ──────────────────────────────────────

function inventoryPosition(sku, branchCode, onHand, reserved, onOrder) {
  const wh = INFOR_BRANCH_MAP[branchCode];
  return {
    sku, branchCode,
    warehouseId: wh?.warehouseId || branchCode,
    warehouseName: wh?.name || branchCode,
    onHand, reserved, onOrder,
    locationQtyAvailable: Math.max(0, onHand - reserved),
    asOf: new Date().toISOString(),
  };
}

const MOCK_INVENTORY_POSITIONS = [
  inventoryPosition('KB-FAB-MERI-004', 'DEN', 78, 20, 0),
  inventoryPosition('KB-FAB-MERI-004', 'PHX', 24, 8, 20),
  inventoryPosition('KB-FAB-MERI-004', 'ATL', 0, 0, 40),
  inventoryPosition('KB-VIN-2005',     'DEN', 520, 80, 0),
  inventoryPosition('KB-VIN-2005',     'PHX', 280, 40, 0),
  inventoryPosition('KB-VIN-2005',     'DAL', 160, 20, 100),
  inventoryPosition('KB-LEA-1001',     'DEN', 84, 12, 0),
  inventoryPosition('KB-LEA-1002',     'DEN', 27, 8,  20),
  inventoryPosition('KB-FOAM-4001',    'DEN', 48, 6,  0),
];

// ── Mock: customer master ────────────────────────────────────────────────────

const MOCK_CUSTOMERS = [
  { externalId: 'KB-CUST-001', inforCustomerNo: 'KB-CUST-001', name: 'Keyston Bros Fabrics — National Accounts', email: 'national@keystonbros.com', phone: '+1-800-555-0120', billingAddress: '2350 Keyston Way, Commerce, CA 90040', paymentTerms: 'Net30', creditLimit: 500000 }, // foundation-purity-allow: demo customer master seed record; customer name is the demo persona in the Infor CSD mock
  { externalId: 'KB-CUST-002', inforCustomerNo: 'KB-CUST-002', name: 'Pacific Upholstery Supply',                email: 'orders@pacificuph.com',         phone: '+1-415-555-0144', billingAddress: '800 Market St, San Francisco, CA 94102', paymentTerms: 'Net30', creditLimit: 75000 },
  { externalId: 'KB-CUST-003', inforCustomerNo: 'KB-CUST-003', name: 'Southwest Fabric & Trim',                email: 'purchasing@swfabric.com',        phone: '+1-602-555-0199', billingAddress: '4400 N 7th Ave, Phoenix, AZ 85013', paymentTerms: 'Net45', creditLimit: 120000 },
  { externalId: 'KB-CUST-004', inforCustomerNo: 'KB-CUST-004', name: 'Acme Marine Canvas',                     email: 'supply@acmemarine.com',          phone: '+1-305-555-0177', billingAddress: '1200 Marine Blvd, Miami, FL 33101', paymentTerms: 'Net30', creditLimit: 60000 },
  { externalId: 'KB-CUST-005', inforCustomerNo: 'KB-CUST-005', name: 'Western Interiors Group',                email: 'ap@westerninteriors.com',        phone: '+1-303-555-0188', billingAddress: '7700 Cherry Creek Dr, Denver, CO 80246', paymentTerms: 'Net60', creditLimit: 200000 },
  { externalId: 'KB-CUST-006', inforCustomerNo: 'KB-CUST-006', name: 'Heartland Upholstery Wholesale',         email: 'purchasing@heartlanduph.com',    phone: '+1-816-555-0133', billingAddress: '3300 Commerce Dr, Kansas City, MO 64101', paymentTerms: 'Net30', creditLimit: 90000 },
];

// ── Mock: vendor master ───────────────────────────────────────────────────────

const MOCK_VENDORS = [
  { externalId: 'VEND-0077', inforVendorNo: 'VEND-0077', name: 'Tannery International Inc.',        email: 'orders@tanneryintl.com',  phone: '+1-770-555-0101', country: 'US', paymentTerms: 'Net45' },
  { externalId: 'VEND-0088', inforVendorNo: 'VEND-0088', name: 'Rolling Hide Co.',                  email: 'sales@rollinghide.com',   phone: '+1-312-555-0144', country: 'US', paymentTerms: 'Net30' },
  { externalId: 'VEND-0091', inforVendorNo: 'VEND-0091', name: 'Pacific Vinyl Systems LLC',         email: 'trade@pacificvinyl.com',  phone: '+1-213-555-0188', country: 'US', paymentTerms: 'Net30' },
  { externalId: 'VEND-0102', inforVendorNo: 'VEND-0102', name: 'Meridian Textile Group',            email: 'supply@meridiantex.com',  phone: '+1-404-555-0199', country: 'US', paymentTerms: 'Net45' },
  { externalId: 'VEND-0110', inforVendorNo: 'VEND-0110', name: 'Foam Factory Direct',               email: 'orders@foamfactory.com',  phone: '+1-949-555-0165', country: 'US', paymentTerms: 'Net30' },
  { externalId: 'VEND-0115', inforVendorNo: 'VEND-0115', name: 'American Thread & Notions',         email: 'sales@amthread.com',      phone: '+1-704-555-0177', country: 'US', paymentTerms: 'Net30' },
  { externalId: 'VEND-0118', inforVendorNo: 'VEND-0118', name: 'YKK Americas Fasteners',            email: 'trade@ykk-americas.com',  phone: '+1-770-555-0155', country: 'US', paymentTerms: 'Net30' },
];

// ── Mock: purchase orders ─────────────────────────────────────────────────────

const MOCK_POS = [
  {
    externalId: 'KB-PO-8001', poNumber: 'KB-PO-8001', vendorExternalId: 'VEND-0077',
    status: 'open', orderDate: '2026-04-05', expectedDate: '2026-04-28',
    lines: [{ sku: 'KB-LEA-1001', qty: 60, unitCost: 189.50 }, { sku: 'KB-LEA-1002', qty: 40, unitCost: 175.00 }],
  },
  {
    externalId: 'KB-PO-8002', poNumber: 'KB-PO-8002', vendorExternalId: 'VEND-0102',
    status: 'open', orderDate: '2026-04-10', expectedDate: '2026-05-05',
    lines: [{ sku: 'KB-FAB-MERI-004', qty: 20, unitCost: 24.80 }, { sku: 'KB-FAB-MERI-005', qty: 20, unitCost: 24.80 }],
  },
  {
    externalId: 'KB-PO-8003', poNumber: 'KB-PO-8003', vendorExternalId: 'VEND-0091',
    status: 'received', orderDate: '2026-03-20', expectedDate: '2026-04-10',
    lines: [{ sku: 'KB-VIN-2006', qty: 100, unitCost: 6.25 }],
  },
];

// ── Mock: sales orders ────────────────────────────────────────────────────────

const MOCK_SALES_ORDERS = [
  {
    externalId: 'KB-SO-9001', soNumber: 'KB-SO-9001', customerExternalId: 'KB-CUST-001',
    status: 'open', orderDate: '2026-04-15', requestedDate: '2026-04-25',
    lines: [{ sku: 'KB-FAB-MERI-004', qty: 8, unitPrice: 46.00 }, { sku: 'KB-VIN-2005', qty: 200, unitPrice: 11.95 }],
  },
  {
    externalId: 'KB-SO-9002', soNumber: 'KB-SO-9002', customerExternalId: 'KB-CUST-003',
    status: 'picking', orderDate: '2026-04-14', requestedDate: '2026-04-22',
    lines: [{ sku: 'KB-LEA-1001', qty: 5, unitPrice: 329.00 }],
  },
  {
    externalId: 'KB-SO-9003', soNumber: 'KB-SO-9003', customerExternalId: 'KB-CUST-002',
    status: 'shipped', orderDate: '2026-04-10', requestedDate: '2026-04-18',
    lines: [{ sku: 'KB-FOAM-4001', qty: 24, unitPrice: 16.25 }, { sku: 'KB-ZIP-6001', qty: 200, unitPrice: 1.95 }],
  },
];

// ── Mock: invoices ────────────────────────────────────────────────────────────

const MOCK_INVOICES = [
  { externalId: 'KB-INV-5001', invoiceNumber: 'KB-INV-5001', customerExternalId: 'KB-CUST-001', amount: 18440.00, issuedAt: '2026-04-01T00:00:00Z', dueAt: '2026-05-01T00:00:00Z', status: 'open',    balance: 18440.00 },
  { externalId: 'KB-INV-5002', invoiceNumber: 'KB-INV-5002', customerExternalId: 'KB-CUST-002', amount:  4825.00, issuedAt: '2026-04-05T00:00:00Z', dueAt: '2026-05-05T00:00:00Z', status: 'open',    balance:  4825.00 },
  { externalId: 'KB-INV-5003', invoiceNumber: 'KB-INV-5003', customerExternalId: 'KB-CUST-003', amount:  9180.00, issuedAt: '2026-03-28T00:00:00Z', dueAt: '2026-04-27T00:00:00Z', status: 'overdue', balance:  9180.00 },
  { externalId: 'KB-INV-5004', invoiceNumber: 'KB-INV-5004', customerExternalId: 'KB-CUST-004', amount:  2340.00, issuedAt: '2026-04-10T00:00:00Z', dueAt: '2026-05-10T00:00:00Z', status: 'paid',    balance:     0.00 },
];

// ── AEGIS field mapping suggestion (Haiku-assisted, mock-safe) ────────────────
//
// Real: calls Haiku to suggest mappings. Mock: returns preset-based suggestions
// with confidence scores matching AegisFieldMappingService patterns.

export async function suggestFieldMappings(sourceFields) {
  const presetMap = Object.fromEntries(INFOR_FIELD_PRESETS.map(p => [p.sourceField.toLowerCase(), p]));
  return sourceFields.map(field => {
    const normalized = field.toLowerCase().replace(/[^a-z0-9]/g, '');
    const exact = INFOR_FIELD_PRESETS.find(p => p.sourceField.toLowerCase().replace(/[^a-z0-9]/g, '') === normalized);
    if (exact) return { sourceField: field, kairosField: exact.kairosField, confidence: 0.95, method: 'exact' };
    const partial = INFOR_FIELD_PRESETS.find(p =>
      normalized.includes(p.sourceField.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 4))
    );
    if (partial) return { sourceField: field, kairosField: partial.kairosField, confidence: 0.70, method: 'partial' };
    return { sourceField: field, kairosField: null, confidence: 0, method: 'none' };
  });
}

// ── Sync functions ────────────────────────────────────────────────────────────

async function syncItems(connector, { branchFilter } = {}) {
  const items = branchFilter
    ? MOCK_ITEMS.filter(i => i.branchCode === branchFilter)
    : MOCK_ITEMS;
  let count = 0;
  for (const item of items) {
    const wh = INFOR_BRANCH_MAP[item.branchCode] || {};
    await DataService.create('items', {
      ...item,
      warehouseId: wh.warehouseId || item.warehouseId,
      warehouseName: wh.name,
      tenantId: connector.tenantId,
      syncedFromConnectorId: connector.id,
      sourceSystem: SOURCE,
      syncedAt: new Date().toISOString(),
    }, 'infor_csd_sync', SOURCE);
    count++;
  }
  return count;
}

async function syncInventoryPositions(connector) {
  let count = 0;
  for (const pos of MOCK_INVENTORY_POSITIONS) {
    await DataService.create('inventory_positions', {
      ...pos,
      tenantId: connector.tenantId,
      syncedFromConnectorId: connector.id,
      sourceSystem: SOURCE,
      syncedAt: new Date().toISOString(),
    }, 'infor_csd_sync', SOURCE);
    count++;
  }
  try {
    await EventBusService.publish({
      eventType: 'int.infor_csd.inventory.synced',
      tenantId: connector.tenantId,
      sourceModule: 'integrations',
      sourceId: connector.id,
      actor: 'infor_csd_sync',
      payload: { positionCount: count, branches: Object.keys(INFOR_BRANCH_MAP) },
      confidence: 1,
    });
  } catch (_) {}
  return count;
}

async function syncCustomers(connector) {
  let count = 0;
  for (const c of MOCK_CUSTOMERS) {
    await DataService.create('customers', {
      ...c,
      tenantId: connector.tenantId,
      syncedFromConnectorId: connector.id,
      sourceSystem: SOURCE,
      syncedAt: new Date().toISOString(),
      status: 'active',
    }, 'infor_csd_sync', SOURCE);
    count++;
  }
  return count;
}

async function syncVendors(connector) {
  let count = 0;
  for (const v of MOCK_VENDORS) {
    await DataService.create('vendors', {
      ...v,
      tenantId: connector.tenantId,
      syncedFromConnectorId: connector.id,
      sourceSystem: SOURCE,
      syncedAt: new Date().toISOString(),
    }, 'infor_csd_sync', SOURCE);
    count++;
  }
  return count;
}

async function syncPOs(connector) {
  let count = 0;
  for (const po of MOCK_POS) {
    await DataService.create('purchase_orders', {
      ...po,
      tenantId: connector.tenantId,
      syncedFromConnectorId: connector.id,
      sourceSystem: SOURCE,
      syncedAt: new Date().toISOString(),
    }, 'infor_csd_sync', SOURCE);
    count++;
  }
  return count;
}

async function syncSalesOrders(connector) {
  let count = 0;
  for (const so of MOCK_SALES_ORDERS) {
    await DataService.create('sales_orders', {
      ...so,
      tenantId: connector.tenantId,
      syncedFromConnectorId: connector.id,
      sourceSystem: SOURCE,
      syncedAt: new Date().toISOString(),
    }, 'infor_csd_sync', SOURCE);
    count++;
  }
  return count;
}

async function syncInvoices(connector) {
  let count = 0;
  for (const inv of MOCK_INVOICES) {
    await DataService.create('invoices', {
      ...inv,
      tenantId: connector.tenantId,
      syncedFromConnectorId: connector.id,
      sourceSystem: SOURCE,
      syncedAt: new Date().toISOString(),
    }, 'infor_csd_sync', SOURCE);
    count++;
  }
  return count;
}

async function writePOsToInfor(connector, { tenantId, replenishmentSuggestions = [] } = {}) {
  if (!isMockMode(ENV_KEY)) {
    return null; // stub — operator-zone implementation pending
  }
  const suggestions = replenishmentSuggestions.length ? replenishmentSuggestions : [
    { sku: 'KB-FAB-MERI-004', recommendedQty: 20, reasoning: 'atpFree below reorder point — 5-lot, 4YD scenario' },
    { sku: 'KB-VIN-2006',     recommendedQty: 100, reasoning: 'atpFree below reorder point' },
    { sku: 'KB-LEA-1002',     recommendedQty: 40,  reasoning: 'safety stock breach' },
  ];
  const written = suggestions.length;
  const poNumber = `KB-PO-AUTO-${Date.now().toString(36).toUpperCase()}`;

  await DataService.create('purchase_orders', {
    poNumber,
    externalId: poNumber,
    vendorExternalId: 'VEND-0102',
    status: 'draft',
    orderDate: new Date().toISOString().slice(0, 10),
    lines: suggestions.map(s => ({ sku: s.sku, qty: s.recommendedQty, unitCost: 0, reason: s.reasoning })),
    tenantId: tenantId || connector.tenantId,
    syncedFromConnectorId: connector.id,
    sourceSystem: SOURCE,
    aegisGenerated: true,
    syncedAt: new Date().toISOString(),
  }, 'infor_csd_po_writeback', SOURCE);

  try {
    await EventBusService.publish({
      eventType: 'int.infor_csd.po.created',
      tenantId: tenantId || connector.tenantId,
      sourceModule: 'integrations',
      sourceId: connector.id,
      actor: 'infor_csd_po_writeback',
      payload: { poNumber, itemCount: written },
      confidence: 1,
    });
  } catch (_) {}

  return {
    recordsIn: 0,
    recordsOut: written,
    errors: 0,
    status: 'success',
    summary: `Mock PO writeback — ${poNumber} created in Infor CSD (${written} line items)`,
  };
}

async function sync(connector, { direction, branchFilter } = {}) {
  const tenantId = connector.tenantId;

  if (direction === 'outbound') {
    const pending = await DataService.query('replenishment_queue', { status: 'approved', acceleratorId: 'accel-1' }, tenantId);
    return writePOsToInfor(connector, { tenantId, replenishmentSuggestions: pending });
  }

  // Inbound: full sync
  const [items, inv, cust, vend, pos, so, invoices] = await Promise.all([
    syncItems(connector, { branchFilter }),
    syncInventoryPositions(connector),
    syncCustomers(connector),
    syncVendors(connector),
    syncPOs(connector),
    syncSalesOrders(connector),
    syncInvoices(connector),
  ]);

  const total = items + inv + cust + vend + pos + so + invoices;
  return {
    recordsIn: total,
    recordsOut: 0,
    errors: 0,
    status: 'success',
    summary: `Infor CSD sync — ${items} items (incl. Meridian Chenille 5-lot/4YD), ${inv} inventory positions, ${cust} customers, ${vend} vendors, ${pos} POs, ${so} SOs, ${invoices} invoices`,
  };
}

async function healthCheck() {
  if (isMockMode(ENV_KEY)) return { health: 'green', error: null };
  return { health: 'yellow', error: 'Live Infor CSD health check not yet implemented' };
}

IntegrationService.register('infor_csd', {
  label: 'Infor CloudSuite Distribution',
  category: 'erp',
  description: 'Item master, inventory positions, PO sync — Keyston Bros production-quality ERP connector', // foundation-purity-allow: connector registry description; customer name identifies the demo use case in the integration registry
  icon: '🏭',
  envKey: ENV_KEY,
  setupComponent: 'InforCSDSetup',
  defaultInterval: 15,
  directions: ['inbound', 'outbound'],
  capabilities: ['items_sync', 'inventory_pull', 'po_writeback', 'customers_sync', 'vendors_sync', 'sales_orders', 'invoices', 'branch_mapping'],
  sync,
  healthCheck,
  describeSchema: () => Object.keys(MOCK_ITEMS[0]).map(name => ({
    name, entity: 'item', type: typeof MOCK_ITEMS[0][name],
  })),
  branchMap: INFOR_BRANCH_MAP,
  demoMode: {
    tenantId: 'kairos-demo',
    highlightSku: 'KB-FAB-MERI-004',
    highlightNote: 'Meridian Chenille — 5-lot, 4YD each pack size scenario',
    preConfigured: true,
  },
});

export default {
  connectorType: 'infor_csd',
  PRESETS: INFOR_FIELD_PRESETS,
  BRANCH_MAP: INFOR_BRANCH_MAP,
  suggestFieldMappings,
  writePOsToInfor,
};
